import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './KeyRequestModal.css';

const KeyRequestModal = ({ isOpen, onClose, gameId, gameName, currentUser }) => {
  const [loading, setLoading] = useState(false);
  const [formConfig, setFormConfig] = useState(null);
  const [formData, setFormData] = useState({
    platformInfo: {
      primaryPlatform: 'twitch',
      twitchUsername: '',
      twitchFollowers: 0,
      youtubeChannel: '',
      youtubeSubscribers: 0,
      averageViewers: 0,
      contentType: []
    },
    portfolio: {
      recentContent: [{ url: '', description: '' }]
    },
    commitment: {
      streamingSchedule: '',
      estimatedStreamDate: '',
      agreedToStream: false
    },
    message: '',
    customResponses: []
  });

  useEffect(() => {
    if (isOpen && gameId) {
      fetchFormConfig();
      prefillUserData();
    }
  }, [isOpen, gameId]);

  const fetchFormConfig = async () => {
    try {
      const response = await axios.get(`/api/key-requests/form/${gameId}`);
      setFormConfig(response.data.form);
      
      // Initialize custom responses
      if (response.data.form.customQuestions) {
        setFormData(prev => ({
          ...prev,
          customResponses: response.data.form.customQuestions.map(q => ({
            question: q.question,
            answer: ''
          }))
        }));
      }
    } catch (error) {
      alert('This game is not accepting key requests at this time.');
      onClose();
    }
  };

  const prefillUserData = () => {
    if (!currentUser) return;

    const updates = {};

    // Prefill Twitch if verified
    if (currentUser.socialAccounts?.twitch?.verified) {
      updates.twitchUsername = currentUser.socialAccounts.twitch.username;
      updates.twitchVerified = true;
    }

    // Prefill YouTube from social links
    const youtubeLink = currentUser.profile?.socialLinks?.find(link => 
      link.platform === 'youtube' && link.verified
    );
    if (youtubeLink) {
      updates.youtubeChannel = youtubeLink.url;
      updates.youtubeVerified = true;
    }

    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({
        ...prev,
        platformInfo: {
          ...prev.platformInfo,
          ...updates
        }
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('platformInfo.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        platformInfo: {
          ...prev.platformInfo,
          [field]: type === 'number' ? parseInt(value) || 0 : value
        }
      }));
    } else if (name.startsWith('commitment.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        commitment: {
          ...prev.commitment,
          [field]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleContentTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      platformInfo: {
        ...prev.platformInfo,
        contentType: prev.platformInfo.contentType.includes(type)
          ? prev.platformInfo.contentType.filter(t => t !== type)
          : [...prev.platformInfo.contentType, type]
      }
    }));
  };

  const handlePortfolioChange = (index, field, value) => {
    const newContent = [...formData.portfolio.recentContent];
    newContent[index][field] = value;
    setFormData(prev => ({
      ...prev,
      portfolio: { recentContent: newContent }
    }));
  };

  const addPortfolioLink = () => {
    if (formData.portfolio.recentContent.length >= 5) {
      alert('Maximum 5 portfolio links allowed');
      return;
    }

    setFormData(prev => ({
      ...prev,
      portfolio: {
        recentContent: [...prev.portfolio.recentContent, { url: '', description: '' }]
      }
    }));
  };

  const removePortfolioLink = (index) => {
    if (formData.portfolio.recentContent.length <= 1) {
      alert('At least 1 portfolio link required');
      return;
    }

    setFormData(prev => ({
      ...prev,
      portfolio: {
        recentContent: prev.portfolio.recentContent.filter((_, i) => i !== index)
      }
    }));
  };

  const handleCustomResponse = (index, value) => {
    const newResponses = [...formData.customResponses];
    newResponses[index].answer = value;
    setFormData(prev => ({
      ...prev,
      customResponses: newResponses
    }));
  };

  const validateForm = () => {
    const { platformInfo, portfolio, commitment, message, customResponses } = formData;

    // Platform validation
    if (formConfig.requirements.requiresTwitchOrYouTube) {
      if (platformInfo.primaryPlatform === 'twitch' && !platformInfo.twitchUsername) {
        return 'Please provide your Twitch username';
      }
      if (platformInfo.primaryPlatform === 'youtube' && !platformInfo.youtubeChannel) {
        return 'Please provide your YouTube channel URL';
      }
      if (platformInfo.primaryPlatform === 'both' && 
          (!platformInfo.twitchUsername || !platformInfo.youtubeChannel)) {
        return 'Please provide both Twitch and YouTube information';
      }
    }

    // Follower count validation
    const totalFollowers = (platformInfo.twitchFollowers || 0) + (platformInfo.youtubeSubscribers || 0);
    if (totalFollowers < formConfig.requirements.minFollowers) {
      return `You need at least ${formConfig.requirements.minFollowers} total followers/subscribers`;
    }

    // Content type validation
    if (platformInfo.contentType.length === 0) {
      return 'Please select at least one content type';
    }

    // Portfolio validation
    if (!portfolio.recentContent[0].url) {
      return 'Please provide at least one portfolio link';
    }

    // Commitment validation
    if (!commitment.streamingSchedule.trim()) {
      return 'Please describe your streaming schedule';
    }

    if (!commitment.agreedToStream) {
      return 'You must agree to stream the game';
    }

    // Message validation
    if (message.length < 50) {
      return 'Your message must be at least 50 characters';
    }

    // Custom questions validation
    if (formConfig.customQuestions) {
      for (let i = 0; i < formConfig.customQuestions.length; i++) {
        if (formConfig.customQuestions[i].required && !customResponses[i]?.answer.trim()) {
          return `Please answer: ${formConfig.customQuestions[i].question}`;
        }
      }
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      alert(validationError);
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/key-requests', {
        indieGameId: gameId,
        ...formData
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      alert('Key request submitted successfully! The developer will review it and contact you.');
      onClose();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !formConfig) return null;

  const contentTypes = [
    { value: 'lets_play', label: "Let's Play" },
    { value: 'review', label: 'Review' },
    { value: 'first_impression', label: 'First Impression' },
    { value: 'tutorial', label: 'Tutorial' },
    { value: 'speedrun', label: 'Speedrun' },
    { value: 'challenge', label: 'Challenge' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div className="key-request-modal">
      <div className="key-request-modal-content">
        <div className="key-request-modal-header">
          <h2>Request Key for {gameName}</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <div className="key-request-info">
          <p className="info-text">{formConfig.instructions}</p>
          <div className="requirements-box">
            <h4>Requirements:</h4>
            <ul>
              <li>Minimum {formConfig.requirements.minFollowers} total followers/subscribers</li>
              <li>Account age: {formConfig.requirements.minAccountAge} days</li>
              {formConfig.requirements.requiresVerifiedSocial && (
                <li>At least one verified social account</li>
              )}
              {formConfig.requirements.requiresTwitchOrYouTube && (
                <li>Active Twitch or YouTube channel</li>
              )}
            </ul>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="key-request-form">
          {/* Platform Selection */}
          <div className="form-section">
            <h3>Platform Information</h3>
            
            <div className="form-group">
              <label>Primary Platform *</label>
              <select
                name="platformInfo.primaryPlatform"
                value={formData.platformInfo.primaryPlatform}
                onChange={handleInputChange}
                required
              >
                <option value="twitch">Twitch</option>
                <option value="youtube">YouTube</option>
                <option value="both">Both</option>
              </select>
            </div>

            {['twitch', 'both'].includes(formData.platformInfo.primaryPlatform) && (
              <>
                <div className="form-group">
                  <label>Twitch Username *</label>
                  <input
                    type="text"
                    name="platformInfo.twitchUsername"
                    value={formData.platformInfo.twitchUsername}
                    onChange={handleInputChange}
                    placeholder="your_twitch_username"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Twitch Followers *</label>
                  <input
                    type="number"
                    name="platformInfo.twitchFollowers"
                    value={formData.platformInfo.twitchFollowers}
                    onChange={handleInputChange}
                    min="0"
                    required
                  />
                </div>
              </>
            )}

            {['youtube', 'both'].includes(formData.platformInfo.primaryPlatform) && (
              <>
                <div className="form-group">
                  <label>YouTube Channel URL *</label>
                  <input
                    type="url"
                    name="platformInfo.youtubeChannel"
                    value={formData.platformInfo.youtubeChannel}
                    onChange={handleInputChange}
                    placeholder="https://youtube.com/@yourchannel"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>YouTube Subscribers *</label>
                  <input
                    type="number"
                    name="platformInfo.youtubeSubscribers"
                    value={formData.platformInfo.youtubeSubscribers}
                    onChange={handleInputChange}
                    min="0"
                    required
                  />
                </div>
              </>
            )}

            <div className="form-group">
              <label>Average Viewers/Views *</label>
              <input
                type="number"
                name="platformInfo.averageViewers"
                value={formData.platformInfo.averageViewers}
                onChange={handleInputChange}
                min="0"
                required
              />
              <small>Average concurrent viewers (Twitch) or views per video (YouTube)</small>
            </div>

            <div className="form-group">
              <label>Content Type * (Select all that apply)</label>
              <div className="content-type-grid">
                {contentTypes.map(type => (
                  <label key={type.value} className="checkbox-option">
                    <input
                      type="checkbox"
                      checked={formData.platformInfo.contentType.includes(type.value)}
                      onChange={() => handleContentTypeChange(type.value)}
                    />
                    <span>{type.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Portfolio */}
          <div className="form-section">
            <h3>Content Portfolio</h3>
            <small>Provide 1-5 links to recent content (streams/videos of similar games)</small>
            
            {formData.portfolio.recentContent.map((content, index) => (
              <div key={index} className="portfolio-item">
                <div className="portfolio-inputs">
                  <input
                    type="url"
                    placeholder="https://twitch.tv/videos/... or youtube.com/watch?v=..."
                    value={content.url}
                    onChange={(e) => handlePortfolioChange(index, 'url', e.target.value)}
                    required={index === 0}
                  />
                  <input
                    type="text"
                    placeholder="Brief description (optional)"
                    value={content.description}
                    onChange={(e) => handlePortfolioChange(index, 'description', e.target.value)}
                    maxLength="100"
                  />
                </div>
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => removePortfolioLink(index)}
                    className="remove-portfolio-btn"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}

            {formData.portfolio.recentContent.length < 5 && (
              <button type="button" onClick={addPortfolioLink} className="add-portfolio-btn">
                + Add Another Link
              </button>
            )}
          </div>

          {/* Commitment */}
          <div className="form-section">
            <h3>Streaming Commitment</h3>
            
            <div className="form-group">
              <label>Streaming Schedule *</label>
              <textarea
                name="commitment.streamingSchedule"
                value={formData.commitment.streamingSchedule}
                onChange={handleInputChange}
                placeholder="e.g., Tuesdays and Thursdays 7-10 PM EST"
                maxLength="300"
                rows="3"
                required
              />
            </div>

            <div className="form-group">
              <label>Estimated Stream Date</label>
              <input
                type="date"
                name="commitment.estimatedStreamDate"
                value={formData.commitment.estimatedStreamDate}
                onChange={handleInputChange}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <label className="checkbox-label required-checkbox">
              <input
                type="checkbox"
                name="commitment.agreedToStream"
                checked={formData.commitment.agreedToStream}
                onChange={handleInputChange}
                required
              />
              <span>I agree to stream/feature this game if my request is approved *</span>
            </label>
          </div>

          {/* Message */}
          <div className="form-section">
            <h3>Your Message</h3>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Tell the developer why you'd like to feature their game and what makes your content unique... (50-1000 characters)"
              minLength="50"
              maxLength="1000"
              rows="6"
              required
            />
            <small>{formData.message.length}/1000 characters (minimum 50)</small>
          </div>

          {/* Custom Questions */}
          {formConfig.customQuestions && formConfig.customQuestions.length > 0 && (
            <div className="form-section">
              <h3>Additional Questions</h3>
              {formConfig.customQuestions.map((question, index) => (
                <div key={index} className="form-group">
                  <label>
                    {question.question} {question.required && '*'}
                  </label>
                  <textarea
                    value={formData.customResponses[index]?.answer || ''}
                    onChange={(e) => handleCustomResponse(index, e.target.value)}
                    rows="3"
                    required={question.required}
                  />
                </div>
              ))}
            </div>
          )}

          <div className="form-actions">
            <button type="submit" disabled={loading} className="submit-request-btn">
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
            <button type="button" onClick={onClose} className="cancel-btn">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default KeyRequestModal;
